// ============================================================
// HABIT OS — Bibliothek: Protein Guide
// Wie viel · Tierische Quellen · Pflanzliche Quellen
// Snacks · Protein-Kombos · Timing
// Inspiriert von: @thenourishingplate_ & Functional Medicine
// ============================================================

export const LIBRARY_PROTEIN = {
  id: "protein",
  label: "Protein Guide",
  icon: "🥩",
  subcategories: [

    // ── GRUNDLAGEN ───────────────────────────
    {
      id: "protein_basics",
      label: "Grundlagen & Bedarf",
      icon: "📊",
      habits: [
        {
          id: "hab_prot_daily_target",
          label: "Protein-Tages-Ziel berechnen & treffen",
          time: "flexible",
          icon: "🎯",
          why: "Protein ist ein wichtiger Baustein für Muskeln. Der Bedarf hängt von Alter, Aktivität und Gesundheit ab.",
          source: "@thenourishingplate_ · Attia P., Outlive, 2023 · Lyon G., Forever Strong, 2023",
          dose: "Orientierung: DGE-Referenzwert 0,8 g pro kg Körpergewicht, bei viel Sport ggf. mehr. Höhere Mengen vorher ärztlich abklären.",
        },
        {
          id: "hab_prot_no_skip_meals",
          label: "Mahlzeiten nicht auslassen",
          time: "flexible",
          icon: "⏰",
          why: "Regelmäßige Mahlzeiten können helfen, Heißhunger zu vermeiden.",
          source: "@thenourishingplate_ · Leidy et al., American Journal of Clinical Nutrition, 2013",
          dose: "3 vollständige Mahlzeiten täglich + optional 1–2 proteinreiche Snacks. Nicht mehr als 4–5 Std. ohne Protein-Einnahme",
        },
        {
          id: "hab_prot_quality_matters",
          label: "Protein-Qualität: Grasfed, Pastured, Wild",
          time: "flexible",
          icon: "🌿",
          why: "Rindfleisch aus Weidehaltung kann ein etwas günstigeres Fettsäureprofil haben. Fisch und Eier sind weitere gute Proteinquellen.",
          source: "@thenourishingplate_ · Daley et al., Nutrition Journal, 2010",
          dose: "Priorität: Wild Caught Fish > Pasture-raised Eier > Grass-fed Beef > Organic Geflügel — Budget-Fokus auf diese Reihenfolge",
        },
      ],
    },

    // ── TIERISCHE PROTEIN-QUELLEN ────────────
    {
      id: "protein_animal",
      label: "Tierische Quellen",
      icon: "🐟",
      habits: [
        {
          id: "hab_prot_wild_salmon",
          label: "Wilder Lachs 2–3x pro Woche",
          time: "flexible",
          icon: "🐟",
          why: "Wildlachs liefert Protein, die Omega-3-Fettsäuren EPA und DHA sowie Vitamin D und B12.",
          source: "@thenourishingplate_ · Attia P., Outlive, 2023",
          dose: "120 g Wild Caught Lachs, 2–3x pro Woche — Alaska Sockeye oder Coho für niedrigsten Quecksilber-Gehalt",
        },
        {
          id: "hab_prot_eggs_daily",
          label: "Pasture-Raised Eier täglich",
          time: "morning",
          icon: "🥚",
          why: "Eier sind die biologisch vollständigste Protein-Quelle — mit dem höchsten PDCAAS (Protein Digestibility Corrected Amino Acid Score) aller natürlichen Lebensmittel. Ein Ei liefert 6–7 g vollständiges Protein plus Cholin (essenziell für Gedächtnis, Leberfunktion und fetalen Gehirnentwicklung — 90% der Menschen konsumieren zu wenig), Leucin (Schlüssel für Muskelproteinsynthese) und alle fettlöslichen Vitamine.",
          source: "@thenourishingplate_ · Zeisel & da Costa, Nutrition Reviews, 2009",
          dose: "2–4 Eier täglich, morgens — ganzes Ei essen, nicht nur Eiweiß! Das Eigelb enthält 90% der Nährstoffe",
        },
        {
          id: "hab_prot_greek_yogurt",
          label: "Griechischer Joghurt täglich",
          time: "morning",
          icon: "🥛",
          why: "Griechischer Joghurt liefert 23–25 g Protein pro Tasse — doppelt so viel wie normaler Joghurt — durch den Konzentrierungs-Prozess. Er enthält gleichzeitig lebende Probiotika-Kulturen (L. acidophilus, Bifidobacterium), Kalzium und Casein-Protein (langsam verdaulich, hält Sättigung länger als Whey). Vollfett-Varianten enthalten zusätzlich CLA und fettlösliche Vitamine.",
          source: "@thenourishingplate_ · Leidy et al., American Journal of Clinical Nutrition, 2013",
          dose: "200 g täglich (1 Tasse) — Vollfett bevorzugen, keine Aromen oder Zuckerzusätze, Marken mit aktiven Kulturen",
        },
        {
          id: "hab_prot_bone_broth_protein",
          label: "Knochenbrühe als Protein-Snack",
          time: "flexible",
          icon: "🍲",
          why: "Knochenbrühe enthält Protein in Form von Kollagen bzw. Gelatine. Sie ist ein warmer, sättigender Snack.",
          source: "@thenourishingplate_ · Rennard et al., Chest, 2000 · Shaw et al., Nutrients, 2017",
          dose: "1 Tasse täglich als Snack oder zum Frühstück — selbst aus Knochen kochen (24–48 Std.) oder fertig gekauft",
        },
        {
          id: "hab_prot_tuna_sardines",
          label: "Wildfang-Thunfisch & Sardinen als Snack",
          time: "flexible",
          icon: "🐠",
          why: "Thunfisch und Sardinen sind praktische Proteinquellen. Thunfisch kann mehr Quecksilber enthalten; Schwangere sollten die Hinweise des BfR beachten.",
          source: "@thenourishingplate_ · Environmental Defense Fund Seafood Guide, 2023",
          dose: "2–4 Dosen pro Woche: Thunfisch in Wasser oder Olivenöl · Sardinen in Olivenöl mit Gräten. Schwangere: Verzehrhinweise des BfR zu Thunfisch beachten",
        },
      ],
    },

    // ── PFLANZLICHE PROTEIN-QUELLEN ──────────
    {
      id: "protein_plant",
      label: "Pflanzliche Quellen",
      icon: "🌱",
      habits: [
        {
          id: "hab_prot_lentils",
          label: "Linsen 3–4x pro Woche",
          time: "flexible",
          icon: "🫘",
          why: "Linsen sind das Kraft-Paket der pflanzlichen Proteinquellen: 18 g Protein pro Tasse (gekocht), 16 g Ballaststoffe, Eisen, Folat und Präbiotika. Sie haben den niedrigsten glykämischen Index aller Hülsenfrüchte und ihr Protein enthält gute Leucin-Mengen. Linsen sind außerdem die günstigste Protein-Quelle pro Gramm verfügbar. Einweichen über Nacht reduziert Phytinsäure und verbessert Nährstoff-Absorption.",
          source: "@thenourishingplate_ · USDA FoodData Central · Afshin et al., BMJ, 2019",
          dose: "150–200 g gekochte Linsen (ca. 3/4 Tasse), 3–4x pro Woche — über Nacht einweichen, mit Vitamin C-reichem Gemüse essen für bessere Eisen-Absorption",
        },
        {
          id: "hab_prot_hemp_seeds",
          label: "Hanfsamen täglich (3 EL)",
          time: "morning",
          icon: "🌿",
          why: "Hanfsamen liefern pflanzliches Protein mit allen essenziellen Aminosäuren sowie Magnesium und Zink.",
          source: "@thenourishingplate_ · Callaway JC, Euphytica, 2004",
          dose: "3 EL täglich, morgens: in Joghurt, Smoothies, Salate oder über Haferflocken — kühl lagern da Öle schnell ranzig werden",
        },
        {
          id: "hab_prot_tofu_tempeh",
          label: "Tofu oder Tempeh als Protein-Hauptmahlzeit",
          time: "flexible",
          icon: "🧱",
          why: "Tempeh und Tofu liefern vollständiges pflanzliches Protein.",
          source: "@thenourishingplate_ · Shu et al., Journal of the American Medical Association, 2009",
          dose: "150 g Tempeh oder fester Tofu, 3–4x pro Woche — Tempeh mariniert und geröstet als Fleisch-Alternative",
        },
        {
          id: "hab_prot_quinoa",
          label: "Quinoa als vollständiges Protein-Getreide",
          time: "flexible",
          icon: "🌾",
          why: "Quinoa ist das einzige Pseudogetreide mit vollständigem Aminosäure-Profil (alle 9 essentiellen Aminosäuren) — einzigartig unter pflanzlichen Quellen. 8 g Protein pro Tasse plus Magnesium (118 mg), Eisen, Zink und B-Vitamine. Glutenfrei, gut verträglich und mit niedrigem glykämischen Index. Quinoa enthält Quercetin und Kaempferol — Flavonoide mit anti-inflammatorischen Eigenschaften.",
          source: "@thenourishingplate_ · Vega-Gálvez et al., Food Chemistry, 2010",
          dose: "150–200 g täglich als Basis: vor dem Kochen immer spülen um Saponine (bittere Schutzstoffe) zu entfernen",
        },
      ],
    },

    // ── PROTEIN-SNACKS ───────────────────────
    {
      id: "protein_snacks",
      label: "Protein-Snacks",
      icon: "🥜",
      habits: [
        {
          id: "hab_prot_snack_habit",
          label: "Protein-reiche Snacks zwischen den Mahlzeiten",
          time: "flexible",
          icon: "🥜",
          why: "Ein proteinreicher Snack kann helfen, bis zur nächsten Mahlzeit satt zu bleiben.",
          source: "@thenourishingplate_ · Leidy et al., Obesity, 2011",
          dose: "1–2 Snacks täglich, wenn Hunger zwischen den Mahlzeiten entsteht. Nicht aus Gewohnheit snacken — nur wenn wirklich hungrig",
        },
        {
          id: "hab_prot_greek_yogurt_snack",
          label: "Greek Yogurt + Beeren (25 g Protein-Snack)",
          time: "flexible",
          icon: "🫐",
          why: "Griechischer Joghurt mit Beeren ist ein proteinreicher Snack mit Calcium.",
          source: "@thenourishingplate_ · Devore et al., Annals of Neurology, 2012",
          dose: "200 g Vollfett-Joghurt + 100 g Blaubeeren + optional: 1 EL Honig + 1 EL Kürbiskerne für extra Magnesium",
        },
        {
          id: "hab_prot_cottage_cheese",
          label: "Hüttenkäse als Abend-Protein",
          time: "night",
          icon: "🧀",
          why: "Hüttenkäse enthält viel Casein, ein langsam verdauliches Milchprotein.",
          source: "@thenourishingplate_ · Res et al., Medicine & Science in Sports & Exercise, 2012",
          dose: "150–200 g Hüttenkäse abends (1–2 Std. vor dem Schlafen) — mit Zimt und Beeren oder als herzhafter Snack mit Gurke",
        },
        {
          id: "hab_prot_edamame",
          label: "Edamame als Snack (17 g Protein)",
          time: "flexible",
          icon: "🫛",
          why: "Edamame — junge Sojabohnen — sind ein vollständiges Protein mit 17 g pro Tasse, reichem Ballaststoff-Gehalt und hohem Kaliumspiegel. Sie sind minimal verarbeitet (im Gegensatz zu Soja-Isolaten) und enthalten natürliche Isoflavone in einem für den Körper handhabbaren Verhältnis. Als warmer Snack mit Meersalz ist Edamame sättigend, nährstoffreich und extrem bequem.",
          source: "@thenourishingplate_ · USDA FoodData Central",
          dose: "1 Tasse täglich als Snack: tiefgekühltes Edamame, 5 Min. kochen oder dämpfen, Meersalz drüber — fertig",
        },
      ],
    },

    // ── PROTEIN-TIMING & KOMBOS ──────────────
    {
      id: "protein_timing",
      label: "Timing & Kombos",
      icon: "⏰",
      habits: [
        {
          id: "hab_prot_post_workout",
          label: "Protein nach dem Training",
          time: "flexible",
          icon: "💪",
          why: "Eine proteinreiche Mahlzeit rund ums Training unterstützt die Erholung. Ein striktes 30-Minuten-Fenster ist laut Studienlage nicht nötig.",
          source: "Cribb & Hayes, Medicine & Science in Sports & Exercise, 2006 · Schoenfeld et al., Journal of the International Society of Sports Nutrition, 2013",
          dose: "Eine proteinreiche Mahlzeit oder ein Snack in den Stunden nach dem Training: Whey-Shake + Banane, Eier + Kartoffel, oder Joghurt + Beeren",
        },
        {
          id: "hab_prot_smoked_salmon_avocado",
          label: "Lachs + Avocado Kombo (Power-Duo)",
          time: "morning",
          icon: "🐟",
          why: "Lachs liefert Protein, Omega-3 und Vitamin D, Avocado ungesättigte Fette.",
          source: "@thenourishingplate_ · Kopec et al., Molecular Nutrition & Food Research, 2014",
          dose: "60–90 g Räucherlachs + 1/2 Avocado, morgens — auf Sauerteig, mit Kapern und Zitrone oder als Bowl",
        },
        {
          id: "hab_prot_protein_powder_clean",
          label: "Clean Protein Powder (bedarfsweise)",
          time: "flexible",
          icon: "🥤",
          why: "Protein-Pulver ist kein Ersatz für echte Lebensmittel, aber ein praktisches Werkzeug wenn das Protein-Ziel durch Ernährung allein nicht erreichbar ist. Wichtig ist die Qualität: Whey aus Gras-fed-Kühen (ohne rBGH), Casein oder Plant-Based (Pea + Rice Kombination für vollständiges Aminosäure-Profil). NSF-zertifizierte Produkte vermeiden Schwermetalle und Hormone.",
          source: "@thenourishingplate_ · Hartman et al., American Journal of Clinical Nutrition, 2007",
          dose: "Bei Bedarf eine Portion laut Herstellerangabe. Auf geprüfte Qualität achten (z. B. Kölner Liste).",
        },
      ],
    },
  ],
};